import { createPortal } from 'react-dom';
import { ChevronRight, Plus, Settings, X } from 'lucide-react';
import type { ConnectedChannelItem } from './ConnectedChannelsBar';

interface MarketingPlatformsModalProps {
  open: boolean;
  onClose: () => void;
  channels: ConnectedChannelItem[];
  /** View button — opens the platform's posts view (gbp/facebook/instagram). */
  onViewChannel: (id: string) => void;
  /** Manage / Connect button — opens connection management. */
  onManageChannel: (id: string) => void;
}

/** Channels that have their own posts page inside Marketing. */
const VIEWABLE_CHANNELS = new Set(['gbp', 'facebook', 'instagram', 'linkedin']);
/** Channels whose icon is an official image mark (rendered on white, not a brand-colour disc). */
const IMAGE_MARK_CHANNELS = new Set(['gbp', 'facebook', 'instagram', 'linkedin', 'sendgrid', 'twilio']);

/** Brand avatar backgrounds so each profile reads like its actual platform. */
const BRAND_AVATAR: Record<string, string> = {
  instagram:
    'radial-gradient(circle at 30% 110%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
  facebook: '#1877F2',
  gbp: '#4285F4',
  sendgrid: '#1A82E2',
  twilio: '#F22F46',
};

function ProfileRow({
  channel,
  onView,
  onManage,
}: {
  channel: ConnectedChannelItem;
  onView: () => void;
  onManage: () => void;
}) {
  const Icon = channel.icon;
  const connected = channel.status === 'connected';
  const pending = channel.status === 'pending';
  const active = connected || pending;
  // Multi-location Google rows carry the connection id after a colon.
  const baseId = channel.id.split(':')[0];
  const viewable = connected && VIEWABLE_CHANNELS.has(baseId);

  const primary = active ? channel.profileName || channel.label : channel.label;
  const secondary = active
    ? channel.profileHandle
      ? `@${channel.profileHandle}`
      : channel.detail || (pending ? 'Pending verification' : channel.label)
    : 'Not connected';

  return (
    <button
      type="button"
      onClick={viewable ? onView : onManage}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-left active:bg-gray-50 transition-colors touch-manipulation"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Brand avatar with presence badge */}
      <div className="relative shrink-0">
        <div
          className={`flex h-[46px] w-[46px] items-center justify-center rounded-full overflow-hidden ${
            IMAGE_MARK_CHANNELS.has(baseId) ? 'bg-white border border-gray-200' : ''
          } ${active ? '' : 'opacity-45 grayscale'}`}
          style={
            IMAGE_MARK_CHANNELS.has(baseId)
              ? undefined
              : { background: BRAND_AVATAR[baseId] || '#111827' }
          }
        >
          {IMAGE_MARK_CHANNELS.has(baseId) ? (
            <Icon className="h-[46px] w-[46px]" />
          ) : (
            <Icon className="h-[22px] w-[22px] text-white" strokeWidth={2.1} />
          )}
        </div>
        {connected ? (
          <span
            className="absolute -bottom-px -right-px h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white"
            aria-hidden
          />
        ) : pending ? (
          <span
            className="absolute -bottom-px -right-px h-3.5 w-3.5 rounded-full bg-amber-400 border-2 border-white"
            aria-hidden
          />
        ) : null}
      </div>

      {/* Identity */}
      <div className="min-w-0 flex-1">
        <div
          className={`truncate text-[15px] font-semibold ${active ? 'text-gray-900' : 'text-gray-500'}`}
          style={{ letterSpacing: '-0.01em' }}
        >
          {primary}
        </div>
        <div className={`truncate text-[13px] ${active ? 'text-gray-500' : 'text-gray-400'}`}>
          {active ? secondary : `${channel.label} · Not connected`}
        </div>
      </div>

      {/* Trailing affordances */}
      {active ? (
        <div className="flex shrink-0 items-center gap-1">
          <span
            role="button"
            tabIndex={0}
            aria-label={`Manage ${channel.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onManage();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onManage();
              }
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 active:bg-gray-100 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Settings className="h-[17px] w-[17px]" strokeWidth={2} />
          </span>
          {viewable && (
            <ChevronRight className="h-[18px] w-[18px] text-gray-300" strokeWidth={2.2} />
          )}
        </div>
      ) : (
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-600 px-3.5 py-1.5 text-[13px] font-semibold text-white"
          style={{ letterSpacing: '-0.01em' }}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          Connect
        </span>
      )}
    </button>
  );
}

/**
 * Profiles sheet for the marketing bottom nav — styled like a native social
 * app account switcher: brand avatars with presence badges, profile name +
 * @handle, tap a row to open its posts, gear to manage the connection.
 */
export default function MarketingPlatformsModal({
  open,
  onClose,
  channels,
  onViewChannel,
  onManageChannel,
}: MarketingPlatformsModalProps) {
  if (!open) return null;

  const visible = channels.filter((c) => !c.hidden);
  const activeChannels = visible.filter((c) => c.status === 'connected' || c.status === 'pending');
  const inactiveChannels = visible.filter(
    (c) => c.status !== 'connected' && c.status !== 'pending',
  );

  // Portaled to <body> so no ancestor stacking context can drop it below the
  // floating action button stack.
  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
      onClick={onClose}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
        // Above the floating action button stack (z-50 / z-[60])
        zIndex: 100010,
      }}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-[24px] sm:rounded-[24px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* iOS-style grabber */}
        <div className="pt-2.5 pb-1 flex justify-center sm:hidden" aria-hidden>
          <div className="h-[5px] w-9 rounded-full bg-gray-300" />
        </div>

        <div className="flex items-center justify-between px-5 pt-2 sm:pt-4 pb-1.5">
          <h3 className="text-[19px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
            Profiles
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto pb-3">
          {activeChannels.map((channel) => (
            <ProfileRow
              key={channel.id}
              channel={channel}
              onView={() => onViewChannel(channel.id)}
              onManage={() => onManageChannel(channel.id)}
            />
          ))}

          {inactiveChannels.length > 0 && (
            <>
              <p
                className={`px-5 pb-1.5 text-[12px] font-semibold uppercase tracking-wider text-gray-400 ${
                  activeChannels.length > 0 ? 'pt-3 mt-1 border-t border-gray-100' : 'pt-1'
                }`}
              >
                Add profiles
              </p>
              {inactiveChannels.map((channel) => (
                <ProfileRow
                  key={channel.id}
                  channel={channel}
                  onView={() => onViewChannel(channel.id)}
                  onManage={() => onManageChannel(channel.id)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
